import { SCROLLING_LIST_ITEM_CLASS } from '~/components/shared/ScrollingList'
import { CallTableColumn } from '~/pages/Calls/data/CallTableData'
import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

// import { callsComponentColorKeys } from './_colors'

const callsTableStyleParts = [
  CallTableColumn.DateTime,
  CallTableColumn.Outcome,
  CallTableColumn.Contact,
  CallTableColumn.Duration,
  CallTableColumn.Transcript,
] as const

const helpers = createMultiStyleConfigHelpers(callsTableStyleParts)

//voicemail needs to go away at any breakpoint below large
export const CallsTableStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    dateTimeColumn: {
      transition: 'all 0.4s ease-in-out',
    },
    outcomeColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    contactColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    durationColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    transcriptColumn: {
      transition: 'all 0.15s ease-in-out',
    },
  },
  sizes: {
    [Size.sm]: {
      dateTimeColumn: {
        minWidth: '6.625rem',
        maxWidth: '7rem',
      },
      durationColumn: {
        minWidth: '5rem',
        maxWidth: '5rem',
      },
      outcomeColumn: {
        minWidth: '4.5rem',
        maxWidth: '5.5rem',
      },
      contactColumn: {
        minWidth: '10.5rem',
      },
      transcriptColumn: {
        minWidth: '4rem',
        maxWidth: '4rem',
      },
    },
    [Size.lg]: {
      dateTimeColumn: {
        minWidth: '6.625rem',
        maxWidth: '12.5rem',
      },
      durationColumn: {
        minWidth: '5.5rem',
      },
      outcomeColumn: {
        minWidth: '5.25rem',
        maxWidth: '12.5rem',
      },
      contactColumn: {
        minWidth: '10.5rem',
      },
      transcriptColumn: {
        minWidth: '4rem',
        maxWidth: '4rem',
      },
    },
    [Size.md]: {
      dateTimeColumn: {
        minWidth: '6.625rem',
        maxWidth: '10rem',
      },
      durationColumn: {
        minWidth: '5.5rem',
      },
      outcomeColumn: {
        minWidth: '7.5rem',
        maxWidth: '12.5rem',
      },
      contactColumn: {
        minWidth: '10.5rem',
      },
      transcriptColumn: {
        minWidth: '4rem',
        maxWidth: '4rem',
      },
    },
  },
})

export const useCallTableStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(CallsTableStyle, props) as MultiStyleType<
    typeof callsTableStyleParts
  >

const tableHeaderCellParts = [
  'container',
  'tableHeader',
  'headerColumnWrapper',
  'headerText',
  'sortIcon',
  'listContainer',
  CallTableColumn.DateTime,
  CallTableColumn.Outcome,
  CallTableColumn.Contact,
  CallTableColumn.Duration,
  CallTableColumn.Transcript,
] as const

const cellHelpers = createMultiStyleConfigHelpers(tableHeaderCellParts)

//voicemail needs to go away at any breakpoint below large
export const TableHeaderCellStyle = cellHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 0 0',
      pt: '1.25rem',
      pl: '1.25rem',
      pr: '.25rem', //scrollbar adds extra space, we want the sides to match in total
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      userSelect: 'none',
      backgroundColor: 'bg.subtle',
    },
    tableHeader: {
      display: 'flex',
      height: '2.5rem',
      pl: '1rem',
      pr: '2.25rem', //align header bottom border with row bottom borders
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.625rem',
      alignSelf: 'stretch',
    },
    headerColumnWrapper: {
      display: 'flex',
      height: '2.5rem',
      alignItems: 'center',
      flexShrink: 0,
      alignSelf: 'stretch',
      borderBottom: '1px solid',
      borderBottomColor: 'border.default',
    },
    dateTimeColumn: {
      display: 'flex',
      flex: '1 0 0',
      alignItems: 'center',
      alignSelf: 'stretch',
      py: '0.75rem',
      pr: '1rem',
      gap: '0.5rem',
    },
    outcomeColumn: {
      display: 'flex',
      flex: '1 0 0',
      alignItems: 'center',
      alignSelf: 'stretch',
      py: '0.75rem',
      gap: '0.75rem',
    },
    voicemailColumn: {
      display: 'flex',
      flex: '1 0 0',
      alignSelf: 'stretch',
      py: '0.75rem',
      gap: '0.75rem',
    },
    contactColumn: {
      display: 'flex',
      flex: '1 0 0',
      alignItems: 'center',
      alignSelf: 'stretch',
      py: '0.75rem',
      gap: '0.75rem',
    },
    durationColumn: {
      display: 'flex',
      flex: '1 0 0',
      alignItems: 'center',
      alignSelf: 'stretch',
      py: '0.75rem',
      gap: '0.5rem',
    },
    toFromColumn: {
      display: 'flex',
      flex: '1 0 0',
      alignItems: 'center',
      alignSelf: 'stretch',
      py: '0.75rem',
      gap: '0.75rem',
    },
    transcriptColumn: {
      display: 'flex',
      flex: '0 1 0',
      alignItems: 'center',
      alignSelf: 'stretch',
      py: '0.75rem',
      gap: '0.75rem',
    },
    headerText: {
      ...textStyles[textStyleKeys.Body3_Bold],
      color: 'fg.default',
    },
    listContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: '1 0 0',
      alignSelf: 'stretch',
      height: 'full',
      overflow: 'hidden',
      backgroundColor: 'bg.muted',
      // Rows overlap by 2px and each one sits in its own transformed list
      // item, so the next row paints over the focused row's bottom ring edge
      // unless the focused item is raised.
      [`& .${SCROLLING_LIST_ITEM_CLASS}:has(:focus-visible)`]: {
        zIndex: 1,
      },
    },
    sortIcon: {
      boxSize: '10px',
      color: 'fg.default',
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        paddingLeft: '1rem',
      },
      tableHeader: {
        paddingLeft: '1rem',
        paddingRight: '1.25rem',
      },
    },
  },
})

export const useTableHeaderCellStyles = (props?: StyleThemingProps) =>
  mergeMultiStyleConfig(TableHeaderCellStyle, props) as MultiStyleType<
    typeof tableHeaderCellParts
  >
